<template>
    <tailwind-layout :title="'Maintenance Request - ' + maintenanceRequest.id">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden md:flex justify-between items-start"><!-- Block for lg and larger -->
                <div class="flex flex-col">
                    <h1 class="font-semibold text-2xl leading-tight mb-4">Maintenance Request #{{ maintenanceRequest.id}} - {{ maintenanceRequest.property.full_address }}</h1>
                </div>
                <div class="flex items-center">
                    <span class="isolate inline-flex rounded-md shadow-sm">
                        <inertia-link
                            :href="$page.props.user.role === 'agent'
                                ? route('properties.show', maintenanceRequest.property_id) + '?tab=maintenance'
                                : route('my-property', { tab: 'maintenance' })"
                            class="whiteButton">
                            <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                        </inertia-link>
                        <button v-if="!maintenanceRequest.deleted_at && $page.props.user.role === 'agent' && !isSharedViewOnly"
                                type="button"
                                class="primaryButton ml-2"
                                @click="showArchiveModal = true;">
                            <span class="">Archive</span>
                        </button>
                        <button v-if="!maintenanceRequest.deleted_at && maintenanceRequest.status !== 'closed' && $page.props.user.role === 'agent' && !isSharedViewOnly"
                                type="button"
                                class="primaryButton ml-2"
                                @click="showCompleteModal = true">
                            <span class="">Mark as Complete</span>
                        </button>
                        <button
                                type="button"
                                class="primaryButton ml-2"
                                @click="downloadChatHistory()">
                            <span class=""> Download Chat History</span>
                        </button>
                    </span>
                </div>
            </div>

            <div class="px-6 lg:px-8 mx-auto flex flex-col justify-center md:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex flex-col my-4">
                    <h1 class="font-semibold text-2xl leading-tight">Maintenance Request #{{ maintenanceRequest.id }} - {{ maintenanceRequest.property.full_address }}</h1>
                    <div class="flex items-center space-x-4">
                        <inertia-link
                            :href="$page.props.user.role === 'agent'
                                ? route('properties.show', maintenanceRequest.property_id) + '?tab=maintenance'
                                : route('my-property', { tab: 'maintenance' })"
                            class="whiteButton">
                            <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                        </inertia-link>
                        <button v-if="!maintenanceRequest.deleted_at && $page.props.user.role === 'agent' && !isSharedViewOnly"
                                type="button"
                                class="primaryButton ml-2"
                                @click="showArchiveModal = true;">
                            <span class="">Archive</span>
                        </button>
                        <button v-if="!maintenanceRequest.deleted_at && $page.props.user.role === 'agent' && !isSharedViewOnly"
                                type="button"
                                class="primaryButton ml-2"
                                @click="showCompleteModal = true">
                            <span>Mark as Complete</span>
                        </button>
                        <button
                                type="button"
                                class="primaryButton ml-2"
                                @click="downloadChatHistory()">
                            <span class=""> Download Chat History</span>
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <!-- <trashed-message v-if="maintenanceRequest.deleted_at" class="mb-6" @restore="showRestoreModal = true;">
            This maintenance request has been archived.
        </trashed-message> -->

        <div class="shadow-xl rounded-lg p-6 bg-white flex flex-col">
            <div class="w-full flex flex-col sm:flex-row">
                <div ref="messagesContainer" class="w-full sm:w-[50%] bg-gray-100 p-4 rounded mt-4 h-96 overflow-y-auto">
                    <div v-for="(group, date) in messages" :key="date" class="mb-6">
                        <!-- Date Separator -->
                        <div class="text-center text-gray-500 text-sm mb-2">
                            {{ date }}
                    </div>

                    <!-- Messages -->
                    <div v-for="msg in group" :key="msg.id" class="flex items-end mb-3"
                        :class="msg.sender_id === user.id ? 'justify-end' : 'justify-start'">

                        <div class="max-w-md p-3 rounded-lg shadow-md"
                            :class="msg.sender_id === user.id
                                ? 'bg-blue-500 text-white rounded-br-none'
                                : 'bg-gray-300 text-black rounded-bl-none'">

                            <div class="text-xs font-semibold mb-1">
                                {{ msg.sender_id === null ? 'Landlord' : (msg.sender_id === user.id ? 'You' : msg.sender_name) }}

                            </div>

                            <p class="text-sm">{{ msg.message }}
                                <br>
                                <span v-if="msg.sender_id === null && maintenanceRequestImages.length > 0" class="text-xs underline cursor-pointer mb-1" @click="viewAllAttachments">
                                    View Attachments
                                </span>
                            </p>

                            <div v-if="msg.attachments.length" class="mt-1">
                                <ul>
                                    <li v-for="file in msg.attachments" :key="file.id">
                                        <a :href="route('maintenance-request-messages.view-attachment', [msg.id, file.id])" target="_blank" class="text-sm underline">View Attachment</a>
                                    </li>
                                </ul>
                            </div>

                            <div class="text-xs mt-1 text-right" :class="msg.sender_id === user.id ? 'text-gray-200' : 'text-gray-700'">
                                {{ msg.time }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="w-full sm:w-[50%] p-4 bg-gray-100 rounded-lg mt-4 sm:ml-4">
                <h3 class="font-semibold text-md leading-tight">Request Type</h3>
                <h2 class="text-gray-500 text-sm">{{ maintenanceRequest?.emergency_type?.name }}</h2>

                <h3 v-if="maintenanceRequest?.emergency_type?.description" class="font-semibold text-md mt-4 leading-tight">Request Description</h3>
                <div v-if="maintenanceRequest?.emergency_type?.description" class="text-gray-500 text-sm">
                    <p v-if="!showFullDescription">{{ truncatedDescription }}</p>
                    <p v-else>{{ maintenanceRequest?.emergency_type?.description }}</p>
                    <button v-if="maintenanceRequest?.emergency_type?.description.split(' ').length > 50"
                            @click="showFullDescription = !showFullDescription"
                            class="text-blue-600 text-sm hover:text-blue-800">
                        {{ showFullDescription ? 'Show Less' : 'Read More' }}
                    </button>
                </div>

                <h3 v-if="maintenanceRequest.other_description" class="font-semibold text-md mt-4 leading-tight">Request Details</h3>
                <div v-if="maintenanceRequest.other_description" class="text-gray-500 text-sm">
                    <p v-if="!showFullOtherDescription">{{ truncatedOtherDescription }}</p>
                    <p v-else>{{ maintenanceRequest.other_description }}</p>
                    <button v-if="maintenanceRequest.other_description.split(' ').length > 50"
                            @click="showFullOtherDescription = !showFullOtherDescription"
                            class="text-blue-600 text-sm hover:text-blue-800">
                        {{ showFullOtherDescription ? 'Show Less' : 'Read More' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Send Message -->
        <form @submit.prevent="sendMessage" class="w-full sm:w-[49.5%] space-y-4 divide-y divide-gray-200 mt-8">
            <fieldset :disabled="isSharedViewOnly" class="contents">
            <div>
                <div>
                    <textarea v-model="form.message"
                        v-bind:class="(form.errors.message)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                        :placeholder="$page.props.user.role === 'agent' ? 'Message your tenant on maintenance updates' : 'Reply to your Landlord here'"
                        rows="10"
                        >
                    </textarea>
                    <p v-text="form.errors.message" class="text-red-500 text-sm"></p>
                </div>
                <div class="mt-2">
                    <input type="file" multiple @input="form.attachments = $event.target.files" accept="image/*,application/pdf"
                        :class="form.errors.attachments ? 'border-red-300' : 'border-gray-300'"
                        class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                    <p class="text-gray-500 text-xs mt-1">To select more than one file hold Ctrl+Shift</p>
                    <div v-if="form.errors" class="mt-1">
                        <div v-for="(error, key, index) in form.errors" :key="key">
                            <p v-if="key.startsWith('attachments.')" class="text-red-500 text-sm">
                                {{ error.replace(key, '') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <form-button :form="form" type="submit">
                Send
            </form-button>
            </fieldset>
            <p v-if="isSharedViewOnly" class="text-sm text-gray-500 mt-2">
                Messaging and request status updates are managed by the listing agent.
            </p>
        </form>
    </div>

        <div v-if="showArchiveModal">
            <TransitionRoot as="template" :show="showArchiveModal">
                <Dialog as="div" class="relative z-50" @close="showArchiveModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to archive this maintenance request?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showArchiveModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="destroy()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <div v-if="showCompleteModal">
            <TransitionRoot as="template" :show="showCompleteModal">
                <Dialog as="div" class="relative z-50" @close="showCompleteModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to mark this maintenance request as complete?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showCompleteModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="markAsComplete()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <div v-if="showRestoreModal">
            <TransitionRoot as="template" :show="showRestoreModal">
                <Dialog as="div" class="relative z-50" @close="showRestoreModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this maintenance request?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="restore()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <Modal :show="showAllAttachments" @close="showAllAttachments = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold mb-4">All Attachments</h3>
                <div class="grid grid-cols-2 gap-4">
                    <div v-for="image in maintenanceRequestImages" :key="image.id" class="relative">
                        <img :src="route('maintenance-request-images.show', image.id)" :alt="image.name" class="w-full h-48 object-cover rounded">
                        <div class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm">
                            {{ image.name }}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import FormButton from '@/Shared/FormButton.vue';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
    faArrowsRotate,
    faBox,
    faBoxArchive,
    faCheckCircle,
    faChevronLeft
} from '@fortawesome/pro-solid-svg-icons';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue";
import TrashedMessage from '@/Shared/TrashedMessage.vue';
import Modal from '@/Jetstream/Modal.vue';

export default {
    props: {
        maintenanceRequest: Object,
        maintenanceRequestImages: Array,
        messages: Object,
        user: Object,
        property_access: {
            type: String,
            default: 'owner',
        },
    },
    components: {
        TailwindLayout,
        FormButton,
        FontAwesomeIcon,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        TrashedMessage,
        Modal
    },
    data() {
        return {
            form: this.$inertia.form({
                message: '',
                attachments: []
            }),
            showArchiveModal: false,
            showCompleteModal: false,
            showRestoreModal: false,
            showFullDescription: false,
            showFullOtherDescription: false,
            showAllAttachments: false,
            faChevronLeft,
            faBox,
            faArrowsRotate,
            faCheckCircle,
        }
    },
    computed: {
        isSharedViewOnly() {
            return this.property_access === 'shared_view'
        },
        truncatedDescription() {
            const description = this.maintenanceRequest.emergency_type.description
            if (!description) return ''
            const words = description.split(' ')
            return words.slice(0, 50).join(' ') + (words.length > 50 ? '...' : '')
        },
        truncatedOtherDescription() {
            const description = this.maintenanceRequest.other_description
            if (!description) return ''
            const words = description.split(' ')
            return words.slice(0, 50).join(' ') + (words.length > 50 ? '...' : '')
        }
    },
    watch: {
        messages: {
            handler() {
                this.scrollToBottom();
            },
            deep: true
        }
    },
    mounted() {
        this.scrollToBottom();
    },
    methods: {
        sendMessage() {
            if (this.isSharedViewOnly) {
                return;
            }
            console.log('send message: ', this.form.attachments);
            this.form.post(this.route('maintenance-request-messages.store', { maintenance_request: this.maintenanceRequest.id }), {
                preserveState: true,
                onSuccess: () => {
                    console.log('success');
                    this.form.reset()
                },
                onError: (errors) => {
                    console.error("Form Errors:", errors);
                }
            });
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.messagesContainer;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },
        downloadChatHistory() {
            window.open(this.route('maintenance-request-messages.download-pdf', {
                maintenance_property: this.maintenanceRequest.property_id,
                maintenance_request: this.maintenanceRequest.id
            }));
        },
        destroy() {
            if (this.isSharedViewOnly) {
                return;
            }

            this.$inertia.delete(this.route('maintenance-requests.destroy', {maintenance_property: this.maintenanceRequest.property.id, maintenance_request: this.maintenanceRequest.id}), {
                onFinish: () => {
                    this.showArchiveModal = false;
                }
            })
        },
        restore() {
            if (this.isSharedViewOnly) {
                return;
            }
            this.$inertia.put(this.route('maintenance-requests.restore', {maintenance_property: this.maintenanceRequest.property.id, maintenance_request: this.maintenanceRequest.id}), {}, {
                onFinish: () => {
                    this.showRestoreModal = false;
                }
            })
        },
        markAsComplete() {
            if (this.isSharedViewOnly) {
                return;
            }
            this.$inertia.put(this.route('maintenance-requests.update', {
                maintenance_property: this.maintenanceRequest.property_id,
                maintenance_request: this.maintenanceRequest.id
            }), {}, {
                onFinish: () => {
                    this.showCompleteModal = false;
                }
            });
        },
        viewAllAttachments() {
            this.maintenanceRequestImages.forEach((image, index) => {
                console.log(index, image);
                setTimeout(() => {
                    window.open(
                        this.route('maintenance-request-images.show', {maintenancePropertyId: this.maintenanceRequest.property_id, imageId: image.id}),
                        '_blank'
                    );
                }, index * 300);
            });
        },
    }
}
</script>
